'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Play } from 'lucide-react';
import type { Media, MuxVideo } from '../../payload-types';
import type { SubjectAttributes } from '../../utilities/change-requests/subject-marks';
import { HlsVideo } from '../shared/mux';
import { MediaElement } from '../shared/media-element';
import { isMedia } from '../../utilities/is-something';
import { getHlsUrl, getPosterUrl, normalizeAspectRatio } from '../../utilities/mux/mux-urls';
import { READING_IMAGE_SIZES } from '../shared/layouts/page-width';

/**
 * The featured video's poster and player. It sets no width of its own: the
 * post page puts it in the reading column, which it fills (`w-full`). The box
 * stays the component's root, since it carries the aspect ratio.
 */
export const BlogHeroVideo: React.FC<{
  video: MuxVideo;
  image?: (string | null) | Media;
  title?: string | null;
  /** The fallback poster `image`'s Subject attribute, from the server parent. */
  imageSubject?: SubjectAttributes;
}> = ({ video, image, title, imageSubject }) => {
  const [playing, setPlaying] = useState(false);

  const playbackOptions = video.playbackOptions?.at(0);
  if (!playbackOptions?.playbackId) return null;

  const hlsSrc = getHlsUrl(playbackOptions);
  if (!hlsSrc) return null;

  const posterUrl = getPosterUrl({ ...playbackOptions, posterTimestamp: video.posterTimestamp });
  const aspectRatio = normalizeAspectRatio(video.aspectRatio) ?? '16 / 9';

  return (
    <div
      className="not-prose relative mb-8 w-full overflow-hidden rounded-box bg-base-200"
      style={{ aspectRatio }}
    >
      {playing ? (
        <HlsVideo
          hlsSrc={hlsSrc}
          controls
          autoPlay
          playsInline
          preload="metadata"
          poster={posterUrl}
          className="h-full w-full object-cover"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={title ? `Play video: ${title}` : 'Play video'}
          className="group absolute inset-0 h-full w-full cursor-pointer"
        >
          {posterUrl ? (
            <Image src={posterUrl} alt="" fill priority sizes={READING_IMAGE_SIZES} className="object-cover" />
          ) : isMedia(image) ? (
            <MediaElement data={image} fill priority sizes={READING_IMAGE_SIZES} className="object-cover" placeholder subject={imageSubject} />
          ) : null}
          <span className="pointer-events-none absolute inset-0 grid place-items-center">
            <span className="grid size-16 place-items-center rounded-full bg-base-100/90 shadow-lg transition-transform group-hover:scale-110">
              <Play className="size-7 translate-x-0.5 fill-current" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
};
